import { PaletteOptions } from '@mui/material/styles'
import { ThemeVariant } from './use-theme-variant'
import { pathspotPrimary, pathspotSecondary } from './colors-pathspot/colors'

export type ExtendedColorPalette = {
  sensor?: {
    critical: string
    warning: string
    inRange: string
    inRangeBackground: string
    outOfRange: string
    outOfRangeBackground: string
    dataText: string
    titleText: string
    subtitleText: string
  }
  thresholds?: {
    critical: string
    medium: string
    low: string
  }
  miscellaneous?: {
    hint: string
    lavender: string
    orange: string
    vibrantGreen: string
    gray: string
  }
  link: {
    text: string
    hover: string
  }
}

enum ThemeVariantColorAttributes {
  primary = 'primary',
  secondary = 'secondary',
  error = 'error',
}

export const colors: PaletteOptions & ExtendedColorPalette = {
  primary: {
    main: '#025c71', // pathspotPrimary, pathspotBlue
    light: '#025c71', // pathspotBlue50
    dark: '#02354a', // derived from primary
    contrastText: '#ffffff', // pathspotWhite
  },
  secondary: {
    main: '#00a29b', // pathspotTeal, pathspotSecondary
    light: '#00a29b', // pathspotTeal50
    dark: '#008079', // derived
    contrastText: '#ffffff', // pathspotWhite
  },
  error: {
    main: '#ff4136', // pathspotRedLight
    light: '#ff4136', // pathspotRedLight
    dark: '#8d1928', // pathspotRedDark
    contrastText: '#ffffff', // pathspotWhite
  },
  warning: {
    main: '#eccc5e', // alertMedium, warningThreshold
    contrastText: '#ffffff', // pathspotWhite
  },
  success: {
    main: '#a4cf59', // pathspotGreen, pathspotLightGreen
    contrastText: '#ffffff', // pathspotWhite
  },
  info: {
    main: '#a55095', // pathspotLavender, rollingAverageData
    contrastText: '#ffffff', // pathspotWhite
  },
  background: {
    default: '#f4f5f8', // pathspotBarWhite
    paper: '#ffffff', // pathspotWhite
  },
  text: {
    primary: '#000000', // pathspotBlack
    secondary: '#596673', // slateGray
    disabled: '#818a98', // pathspotGrayLight
  },
  divider: '#c5cad3', // pathspotGray80
  action: {
    active: '#025c71', // pathspotPrimary
    hover: '#509cad', // pathspotBlue50
    selected: '#72b6b2', // pathspotTeal50
    disabled: '#818a98', // pathspotGrayLight
    disabledBackground: '#f4f5f8', // pathspotBarWhite
  },
  link: {
    text: pathspotPrimary,
    hover: pathspotSecondary,
  },
  sensor: {
    critical: '#fa7a7a', // alertCritical
    warning: '#eccc5e', // alertMedium
    inRange: '#246524', // inRange
    inRangeBackground: '#eefaef', // inRangeBackground
    outOfRange: '#822422', // outOfRange
    outOfRangeBackground: '#ffefee', // outOfRangeBackground
    dataText: '#3c4b64',
    titleText: '#3c4b64',
    subtitleText: '#586274',
  },
  thresholds: {
    critical: '#e74c3c', // toastifyError
    medium: '#f1c40f', // toastifyWarning
    low: '#07bc0c', // toastifySuccess
  },
  miscellaneous: {
    hint: '#73818f', // formPlaceholder
    lavender: '#a55095', // vibrantPurple, averagedData
    orange: '#bf5700', // texasOrange
    vibrantGreen: '#00a29b', // pathspotTeal
    gray: '#596673', // slateGray
  },
}

export const getPaletteByMode = (mode: ThemeVariant) => {
  const themedVariant = Object.values(ThemeVariantColorAttributes).reduce((themedObject, colorAttribute) => {
    const colorDef = colors[colorAttribute] as Record<ThemeVariant, string> | undefined

    if (colorDef?.[mode]) {
      themedObject[colorAttribute] = { main: colorDef[mode] }
    }
    return themedObject
  }, {} as Record<string, { main: string }>)

  return themedVariant
}
